import { useEffect, useRef } from "react";
import { coordsOf, haversineKm, type LatLng } from "@/lib/geo";
import type { Restaurant, RestaurantId } from "@/types/restaurant";

/**
 * Passive conversion capture — a foreground 10-minute low-movement dwell.
 *
 * It replaced the old `visitDetector.ts` stub (deleted in It-3) and keeps its
 * hard constraints: it reuses the app's single foreground `useGeolocation` stream
 * (NO background location, NO extra permission prompt). It fires `onDwell(id)`
 * only when the diner has stood inside a tight radius of a venue they OPENED
 * in-app for a continuous 10 minutes — and never when the fix is imprecise or
 * two venues sit inside the error radius (the clustered-terraces false-positive
 * guard). Web reality: it only accrues while the tab is foreground, so it fires
 * rarely — the "Dine In" tap is the primary signal; this is a quiet backstop.
 *
 * Opt-in only (It-6): `enabled` comes from the Settings switch
 * (`src/lib/autoVisits.ts`), OFF by default. While off, nothing is timed.
 */

const RADIUS_M = 30; // must be this close to count as "at" the venue
const MAX_ACCURACY_M = 35; // ignore fixes worse than this — can't trust them
const DWELL_MS = 10 * 60 * 1000; // continuous time required inside the radius

export function useDwellDetection({
  enabled,
  coords,
  accuracy,
  restaurants,
  isOpened,
  onDwell,
}: {
  /** The diner's opt-in. False → no dwell is ever timed. */
  enabled: boolean;
  coords: LatLng | null;
  accuracy: number | null;
  restaurants: Restaurant[];
  /** True if the diner opened this venue's detail this session (engagement gate). */
  isOpened: (id: RestaurantId) => boolean;
  onDwell: (venueId: RestaurantId) => void;
}) {
  // The venue we're currently timing, its pending timer, and venues already
  // fired this session (so we don't re-convert the same place on every tick).
  const activeVenue = useRef<RestaurantId | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fired = useRef<Set<RestaurantId>>(new Set());

  // Latest values for the timer callback without retriggering the effect.
  const onDwellRef = useRef(onDwell);
  onDwellRef.current = onDwell;

  const reset = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    activeVenue.current = null;
  };

  useEffect(() => {
    // A hidden tab pauses geolocation on the web; drop any in-flight dwell so a
    // backgrounded session can't silently complete a 10-minute count.
    const onVisibility = () => {
      if (document.visibilityState === "hidden") reset();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      reset();
    };
  }, []);

  useEffect(() => {
    if (!enabled || !coords) {
      reset();
      return;
    }
    // Untrustworthy fix → ignore this tick, but keep any running dwell alive
    // (one bad GPS sample shouldn't cancel a legitimate sit-down).
    if (accuracy != null && accuracy > MAX_ACCURACY_M) return;

    // Every venue whose coordinate is inside the radius right now.
    const within = restaurants.filter((r) => {
      const c = coordsOf(r);
      return c != null && haversineKm(coords, c) * 1000 <= RADIUS_M;
    });

    // Exactly one candidate keeps attribution unambiguous; it must be a venue
    // the diner engaged with and one we haven't already counted this session.
    const only = within.length === 1 ? within[0] : null;
    const target =
      only && isOpened(only.id) && !fired.current.has(only.id) ? only.id : null;

    if (!target) {
      reset();
      return;
    }
    if (target === activeVenue.current) return; // already timing this venue

    // New target → start a fresh continuous-dwell timer.
    reset();
    activeVenue.current = target;
    timer.current = setTimeout(() => {
      fired.current.add(target);
      activeVenue.current = null;
      timer.current = null;
      onDwellRef.current(target);
    }, DWELL_MS);
  }, [enabled, coords, accuracy, restaurants, isOpened]);
}
